import * as React from 'react'
import { Cookies } from 'react-cookie'
import { useAsync } from '../hooks/hooks'
import { FullPageSpinner } from '../ui/FullPageSpinner'
import { FullPageErrorFallback } from '../ui/FullPageErrorFallback'
import { client } from '../utils/api-client'
import {
  getAuthTokenFromCookie,
  updateAuthCookie,
  clearAuthCookie,
  isTokenExpiringSoon,
  getValidAccessToken,
} from '../utils/tokenManager'
import { setAuthErrorHandler } from '../utils/tokenManager'
import { mergeWishlist } from '../services/wishlistApi'

async function bootstrapAppData() {
  let user = null
  const authToken = getAuthTokenFromCookie()

  if (!authToken) {
    return null
  }

  try {
    // Get valid access token (will refresh if needed)
    const currentAccessToken = await getValidAccessToken()

    // Fetch user data with current (possibly refreshed) token
    const userData = await client('customer', {
      userToken: currentAccessToken,
    })

    user = userData
  } catch (error) {
    console.error('Error in bootstrapAppData:', error)
    clearAuthCookie()
    return null
  }

  return user
}

let defaultUser = {
  attributes: {},
  cellphone: '',
  createdAt: '',
  customerAddressInfo: [],
  deletedAt: '',
  displayName: '',
  firstName: '',
  id: '',
  lastName: '',
  phone: null,
  updatedAt: '',
  userId: '',
}

export const AuthContext = React.createContext({
  user: defaultUser,
  userLogin: (user: any) => {},
  userRegister: (user: any) => {},
  userLogout: () => {},
  refreshUserToken: () => Promise.resolve(),
  mergeGuestWishlist: () => Promise.resolve(),
})

AuthContext.displayName = 'AuthContext'

const initialState = {
  status: 'idle',
  data: null,
  error: null,

  isIdle: true,
  isLoading: false,
  isError: false,
  isSuccess: false,
}

function AuthProvider(props: any) {
  const {
    data: user,
    status,
    error,
    isLoading,
    isIdle,
    isError,
    isSuccess,
    setData,
    run,
  } = useAsync(initialState)

  const cookies = new Cookies()

  // Setup global auth error handler
  React.useEffect(() => {
    const handleAuthError = () => {
      console.log('Global auth error handler triggered - logging out user')
      // Clear cookie and user data
      cookies.remove('@bookstory-auth-token', { path: '/' })
      setData(null)
      
      // Optionally redirect to login page
      // You can add navigation logic here if needed
    }

    setAuthErrorHandler(handleAuthError)

    // Cleanup
    return () => {
      setAuthErrorHandler(() => {})
    }
  }, [setData])

  React.useEffect(() => {
    const appDataPromise = bootstrapAppData()
    run(appDataPromise)
  }, [run])

  const userLogin = React.useCallback((user: any) => setData(user), [setData])
  const userRegister = React.useCallback(
    (user: any) => setData(user),
    [setData],
  )

  const userLogout = React.useCallback(() => {
    // Clear cookie and user data
    const cookies = new Cookies()
    cookies.remove('@bookstory-auth-token', { path: '/' })
    setData(null)
  }, [setData])

  const mergeGuestWishlist = React.useCallback(async () => {
    try {
      const guestWishlist = localStorage.getItem('guestWishlist')
      if (guestWishlist) {
        const productIds = JSON.parse(guestWishlist)
        if (productIds.length > 0) {
          await mergeWishlist(productIds)
          localStorage.removeItem('guestWishlist')
          console.log('Guest wishlist merged successfully')
        }
      }
    } catch (error) {
      console.error('Failed to merge guest wishlist:', error)
    }
  }, [])

  const refreshUserToken = React.useCallback(async () => {
    try {
      const updatedUser = await bootstrapAppData()
      if (updatedUser) {
        setData(updatedUser)
        return true
      } else {
        userLogout()
        return false
      }
    } catch (error) {
      console.error('Manual token refresh failed:', error)
      userLogout()
      return false
    }
  }, [setData, userLogout])

  const value = React.useMemo(
    () => ({
      user,
      userLogin,
      userRegister,
      userLogout,
      refreshUserToken,
      mergeGuestWishlist,
    }),
    [user, userLogin, userRegister, userLogout, refreshUserToken, mergeGuestWishlist],
  )
  if (isLoading || isIdle) {
    return <FullPageSpinner />
  }

  if (isError) {
    return <FullPageErrorFallback error={error} />
  }

  if (isSuccess) {
    return <AuthContext.Provider value={value} {...props} />
  }

  throw new Error(`Unhandled status: ${status}`)
}

function useAuth() {
  const context = React.useContext(AuthContext)
  if (context === undefined) {
    throw new Error(`useAuth must be used within a AuthProvider`)
  }
  return context
}

export { AuthProvider, useAuth }
